import { isForbiddenError } from '@/context/services/type';
import {
  useGetReconciliationServiceSetting,
  useUpdateReconciliationServiceSettingService,
} from '@/context/services/reconciliation_service_setting/reconciliation_service_setting.service';
import { useErrorTrackingSystem } from '@/hooks/useErrorTrackingSystem';
import { useTranslation } from '@/i18n';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const useReconciliationServiceSetting = () => {
  const { data } = useGetReconciliationServiceSetting();
  const { mutateAsync, isPending } =
    useUpdateReconciliationServiceSettingService();
  const [isStarting, setIsStarting] = useState(false);
  const notify = useToastAPI();
  const { t } = useTranslation();
  const { send } = useErrorTrackingSystem();

  const isEnabled = data?.is_enabled ?? false;
  const apIntegrationServiceId = data?.ap_integration_service_id;

  // Clear isStarting once the refetch confirms isEnabled flipped to true.
  // This keeps the start button in loading state during the fire-and-forget
  // refetch window after the PATCH resolves.
  useEffect(() => {
    if (isEnabled) setIsStarting(false);
  }, [isEnabled]);

  const updateSetting = useCallback(
    async (enable: boolean): Promise<boolean> => {
      const successKey = enable
        ? 'reconciliation_service_setting_start_success'
        : 'reconciliation_service_setting_stop_success';
      const errorKey = enable
        ? 'reconciliation_service_setting_start_error'
        : 'reconciliation_service_setting_stop_error';
      // No matching reconciliation row in the reconciliation settings list — we have
      // no service id to upsert against, so surface the generic error.
      if (!apIntegrationServiceId) {
        notify.error({ title: t(errorKey), duration: 5 });
        return false;
      }
      try {
        const result = await mutateAsync({
          apIntegrationServiceId,
          data: { is_enabled: enable },
        });
        if (result.status === 'success') {
          notify.success({ title: t(successKey), duration: 5 });
          return true;
        }
        if (isForbiddenError(result.statusCode)) {
          const message =
            extractErrorMessage(
              result,
              t,
              'reconciliation_service_setting.error'
            ) ?? t(errorKey);
          notify.error({ title: message, duration: 5 });
          return false;
        }
        notify.error({ title: t(errorKey), duration: 5 });
        return false;
      } catch (error: unknown) {
        if (error instanceof Error) {
          send(error);
        } else {
          send(new Error(String(error)));
        }
        notify.error({ title: t(errorKey), duration: 5 });
        return false;
      }
    },
    [apIntegrationServiceId, mutateAsync, notify, send, t]
  );

  const startIntegration = useCallback(async () => {
    setIsStarting(true);
    const result = await updateSetting(true);
    // If the operation failed, clear immediately — isEnabled won't flip so
    // the useEffect above won't fire.
    if (!result) setIsStarting(false);
    return result;
  }, [updateSetting]);
  const stopIntegration = useCallback(
    () => updateSetting(false),
    [updateSetting]
  );

  return useMemo(
    () => ({
      isEnabled,
      isPending: isPending || isStarting,
      startIntegration,
      stopIntegration,
    }),
    [isEnabled, isPending, isStarting, startIntegration, stopIntegration]
  );
};
